import { FC, useEffect, createContext, useContext, Key } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as _ from "lodash";

import { Optional, isDispatched, isPending } from "@ctra/utils";
import {
  Enterprise,
  EnterpriseAppState,
  FarmEntity,
  SavedCharts,
  SavedChartList,
  MetricEntity,
  Group,
  Filter,
  V3DisplayFilter
} from "@ctra/api";

export type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

interface SavedChartsProviderProps {
  farmID: Optional<FarmEntity["id"]>;
}

type ContextType = {
  savedCharts: SavedChartList;
  api: {
    addTab: (groupName: string) => void;
    updateTabOrder: (order: Array<Key>) => void;
    removeTab: (targetKey: TargetKey) => void;
    renameTab: (targetKey: TargetKey, newName: string) => void;
    saveChart: (
      variantID: MetricEntity["id"],
      groupName: Group["groupName"],
      meta: {
        dateRange: Array<string>;
        chartType: string;
        dataFilters: Array<Filter>;
        displayFilters?: Array<V3DisplayFilter>;
      }
    ) => void;
    saveChartChanges: (
      variantID: MetricEntity["id"],
      groupName: Group["groupName"],
      meta: {
        dateRange: Array<string>;
        chartType: string;
        dataFilters: Array<Filter>;
        displayFilters?: Array<V3DisplayFilter>;
      }
    ) => void;
    removeChart: (variantID: MetricEntity["id"], groupName: Group["groupName"]) => void;
    updateChartOrder: (order: Array<Key>, groupName: Group["groupName"]) => void;
  };
  meta: {
    isLoading: boolean;
    isUpdating: boolean;
    tabsUpdating: boolean;
  };
};

/**
 * Default SavedCharts Context
 */
const DefaultContext = createContext<ContextType>({
  savedCharts: [],
  api: {
    addTab: _.noop,
    removeTab: _.noop,
    renameTab: _.noop,
    updateTabOrder: _.noop,
    saveChart: _.noop,
    saveChartChanges: _.noop,
    removeChart: _.noop,
    updateChartOrder: _.noop
  },
  meta: {
    isLoading: false,
    isUpdating: false,
    tabsUpdating: false
  }
});

/**
 * Fetch saved charts for the given farm or the dashboard
 * @constructor
 */
export const _SavedChartsProvider: FC<SavedChartsProviderProps> = ({ farmID, children }) => {
  const dispatch = useDispatch();

  /**
   * Either get saved charts for a farm or all farms i.e. main
   */
  const pageKey = _.toString(farmID) || "main";
  /**
   * Attempt to get the saved charts
   */
  const savedCharts = useSelector<EnterpriseAppState, SavedChartList>((state) =>
    Enterprise.entities.getSavedCharts(state, { pageKey })
  );

  /**
   * Tell whether the fetching action has been dispatched
   */
  const dispatched = useSelector<EnterpriseAppState, boolean>((state) =>
    isDispatched(state, SavedCharts.types.FETCH_SAVED_CHARTS)
  );

  /**
   * Tell whether the fetching action is pending
   */
  const isLoading = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, SavedCharts.types.FETCH_SAVED_CHARTS)
  );

  /**
   * Tell whether chart list is being updated
   */
  const isUpdating = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, SavedCharts.types.UPDATE_CHARTS)
  );

  /**
   * Tell whether tabs are being updated
   */
  const tabsUpdating = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, SavedCharts.types.UPDATE_TABS)
  );

  useEffect(() => {
    if (!dispatched) {
      dispatch(SavedCharts.actions.fetchSavedCharts.start());
    }
  }, [dispatch, dispatched]);

  /**
   * Add a new tab
   * @param groupName
   */
  const addTab = (groupName: string) => {
    if (_.isArray(savedCharts)) {
      const updatedCharts = [...savedCharts];
      updatedCharts.push({ groupName, charts: [] });

      dispatch(SavedCharts.actions.updateTabs.start(pageKey, updatedCharts));
    }
  };

  /**
   * Remove a tab
   * @param {TargetKey} targetKey
   */
  const removeTab = (targetKey: TargetKey) => {
    const updatedCharts = _.filter(savedCharts, ({ groupName }) => !_.isEqual(groupName, targetKey));

    dispatch(SavedCharts.actions.updateTabs.start(pageKey, updatedCharts));
  };

  /**
   * Change the order of the tabs
   * @param order
   */
  const updateTabOrder = (order: Array<Key>) => {
    const currentOrder = _.map(savedCharts, "groupName");

    if (!_.isEqual(order, currentOrder)) {
      const sortedTabs = _.sortBy(savedCharts, ({ groupName }) =>
        _.findIndex(order, (key) => _.isEqual(key, groupName))
      );

      dispatch(SavedCharts.actions.updateTabs.start(pageKey, sortedTabs));
    }
  };

  /**
   * Change the name of the tab
   * @param {TargetKey} targetKey
   * @param {string} newName
   */
  const renameTab = (targetKey: TargetKey, newName: string) => {
    const updatedTabs = _.map(savedCharts, (group) =>
      group.groupName === targetKey ? { ...group, groupName: newName } : group
    );

    if (!_.isEqual(targetKey, newName)) {
      dispatch(SavedCharts.actions.updateTabs.start(pageKey, updatedTabs));
    }
  };

  /**
   * Save a chart to dashboard
   * @param {MetricEntity["id"]} variantID
   * @param {Group["groupName"]} groupName
   * @param {{dateRange: Array<string>, chartType: string, dataFilters: Array<Filter>, displayFilters?: Array<V3DisplayFilter>}} meta
   */
  const saveChart: ContextType["api"]["saveChart"] = (variantID, groupName, meta) => {
    const newChart = { variantId: variantID, ...meta };

    /**
     * Update the charts within the group
     */
    const updatedCharts = _.map(savedCharts, (group) =>
      _.isEqual(group.groupName, groupName)
        ? { ...group, charts: _.isNull(group.charts) ? [newChart] : [...group?.charts, newChart] }
        : group
    );

    dispatch(SavedCharts.actions.updateCharts.start(pageKey, updatedCharts));
  };

  /**
   * Update existing chart state
   * @param {MetricEntity["id"]} variantID
   * @param {Group["groupName"]} groupName
   * @param {{dateRange: Array<string>, chartType: string, dataFilters: Array<Filter>, displayFilters?: Array<V3DisplayFilter>}} meta
   */
  const saveChartChanges: ContextType["api"]["saveChart"] = (variantID, groupName, meta) => {
    const newChart = { variantId: variantID, ...meta };

    const updatedCharts = _.cloneDeep(savedCharts);

    const group = _.find(updatedCharts, { groupName: groupName });

    if (group) {
      const chartIndex = _.findIndex(group.charts, { variantId: newChart.variantId });
      if (chartIndex !== -1) {
        group.charts[chartIndex] = newChart;
      }
    }

    dispatch(SavedCharts.actions.updateCharts.start(pageKey, updatedCharts));
  };

  /**
   * Save a chart to dashboard
   * @param {MetricEntity["id"]} variantID
   * @param {Group["groupName"]} groupName
   */
  const removeChart = (variantID: MetricEntity["id"], groupName: Group["groupName"]) => {
    /**
     * Update the charts within the group
     */
    const updatedCharts = _.map(savedCharts, (group) =>
      _.isEqual(group.groupName, groupName)
        ? {
            ...group,
            charts: _.filter(group.charts, ({ variantId }) => !_.isEqual(variantId, variantID))
          }
        : group
    );
    dispatch(SavedCharts.actions.updateCharts.start(pageKey, updatedCharts));
  };

  /**
   * Change the order of the charts
   * @param {Array<React.Key>} order
   * @param {Group["groupName"]} groupName
   */
  const updateChartOrder = (order: Array<Key>, groupName: Group["groupName"]) => {
    const group = _.find(savedCharts, { groupName });
    const currentOrder = group && _.map(group.charts, "variantId");

    if (!_.isEqual(order, currentOrder)) {
      const sortedCharts = _.sortBy(group?.charts, ({ variantId }) =>
        _.findIndex(order, (key) => _.isEqual(key, variantId))
      );

      const updatedGroup = { groupName, charts: sortedCharts };

      const reordered = _.map(savedCharts, (group) =>
        _.isEqual(group.groupName, groupName) ? updatedGroup : group
      );

      dispatch(SavedCharts.actions.updateCharts.start(pageKey, reordered));
    }
  };

  return (
    <DefaultContext.Provider
      value={{
        savedCharts,
        api: {
          addTab,
          removeTab,
          renameTab,
          updateTabOrder,
          saveChart,
          saveChartChanges,
          removeChart,
          updateChartOrder
        },
        meta: { isLoading, isUpdating, tabsUpdating }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const SavedChartsContext = {
  Provider: _SavedChartsProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Hook for SavedCharts context
 * @returns
 */
export const useSavedCharts = (): ContextType => useContext<ContextType>(DefaultContext);
